<script setup lang="ts">
type TestData = App.Students.Data.TestData;
type ApplicationData = App.Students.Data.ApplicationData;
type StudentScholarshipData = App.Students.Data.StudentScholarshipData;
type FinancialAidData = App.Students.Data.FinancialAidData;
type DataRequestData = App.DataRequests.Data.DataRequestData;

import PageContainer from '@/components/studentParentApp/container/PageContainer.vue';
import PageSection from '@/components/studentParentApp/PageSection/PageSection.vue';
import SubmissionCardsList from '@/components/studentParentApp/SubmissionCardsList.vue';
import CardList from '@/components/studentParentApp/card/CardList.vue';
import CardListItem from '@/components/studentParentApp/card/CardListItem.vue';

import File from '@/icons/line/file.svg';
import University from '@/icons/line/university.svg';
import AwardAlt from '@/icons/line/award-alt.svg';
import UsdCircle from '@/icons/line/usd-circle.svg';
import { useDataRequests } from '@/domains/student-app/composables/useDataRequests';
import Badge from '@/components/badge/Badge.vue';
import AngleDown from '@/icons/line/angle-down.svg';
import { BadgeColor, BadgeVariant } from '@/hooks/useBadgeClasses';

const props = defineProps<{
  tests: TestData[];
  applications: ApplicationData[];
  scholarships: StudentScholarshipData[];
  financialAid: FinancialAidData;
  activeCreateDataRequests: DataRequestData[];
}>();

const { getRoute } = useDataRequests('financial_aid');

const totalRejectedCount = computed(() => {
  let count = 0;

  if (props.financialAid?.undismissed_rejected_data_request?.id) {
    count++;
  }

  for (const section of ['applications', 'scholarships', 'tests'] as const) {
    props[section].forEach((item) => {
      if (item.undismissed_rejected_data_request?.id) {
        count++;
      }
    });
  }

  count += props.activeCreateDataRequests.filter(
    (item) => item.status?.value === 'rejected'
  ).length;

  return count;
});

const firstRejectedSectionId = computed(() => {
  if (props.financialAid?.undismissed_rejected_data_request?.id) {
    return '#financialAid';
  }

  for (const section of ['applications', 'scholarships', 'tests']) {
    if (
      props[section].some((item) => item.undismissed_rejected_data_request?.id) ||
      props.activeCreateDataRequests.some((item) => item.status?.value === 'rejected')
    ) {
      return `#${section}`;
    }
  }

  return null;
});
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Badge
    v-if="totalRejectedCount > 0 && firstRejectedSectionId"
    :color="BadgeColor.danger"
    :iconRight="AngleDown"
    :label="`${totalRejectedCount} not approved`"
    :href="firstRejectedSectionId"
    class="fixed bottom-21 left-1/2 z-50 -translate-x-1/2"
    :variant="BadgeVariant.solid"
  />
  <PageContainer>
    <div class="flex flex-col gap-y-3 pb-5">
      <CardList headerPadding="tight">
        <template #header>
          <h2 class="text-base font-semibold leading-tight text-slate-900">
            Applied, registered, or received responses for any of the following?
          </h2>
          <p class="mt-0.5 text-sm leading-5 text-slate-600">Submit the details to your adviser.</p>
        </template>

        <CardListItem
          :href="getRoute(props.financialAid)"
          :icon="UsdCircle"
          iconClasses="text-secondary-800/70"
          label="Financial aid"
          labelClasses="text-secondary-900"
        />
        <CardListItem
          :href="route('student.submissions.college-applications.index')"
          :icon="University"
          iconClasses="text-secondary-800/70"
          label="College application"
          labelClasses="text-secondary-900"
        />
        <CardListItem
          :href="route('student.submissions.scholarships.index')"
          :icon="AwardAlt"
          iconClasses="text-secondary-800/70"
          label="Scholarship"
          labelClasses="text-secondary-900"
        />
        <CardListItem
          :href="route('student.submissions.tests.index')"
          :icon="File"
          iconClasses="text-secondary-800/70"
          label="Exam"
          labelClasses="text-secondary-900"
        />
      </CardList>
    </div>
  </PageContainer>
  <PageSection secondary title="Your Submissions">
    <template #navigation>
      <Badge :iconLeft="UsdCircle" class="shrink-0" href="#financialAid">FAFSA</Badge>
      <Badge :iconLeft="University" class="shrink-0" href="#applications"
        >College Applications</Badge
      >
      <Badge :iconLeft="AwardAlt" class="shrink-0" href="#scholarships">Scholarships</Badge>
      <Badge :iconLeft="File" class="shrink-0" href="#tests">Tests</Badge>
    </template>
    <div class="flex flex-col gap-y-4">
      <SubmissionCardsList
        id="financialAid"
        :items="[props.financialAid]"
        type="financial_aid"
        title="FAFSA"
        hasHeader
      />
      <SubmissionCardsList
        id="applications"
        :items="props.applications"
        :activeCreateDataRequests="props.activeCreateDataRequests"
        type="application"
        title="College Applications"
        hasHeader
      />
      <SubmissionCardsList
        id="scholarships"
        :items="props.scholarships"
        :activeCreateDataRequests="props.activeCreateDataRequests"
        type="student_scholarship"
        title="Scholarships"
        hasHeader
      />
      <SubmissionCardsList
        id="tests"
        :items="props.tests"
        :activeCreateDataRequests="props.activeCreateDataRequests"
        type="test"
        title="Tests"
        hasHeader
      />
    </div>
  </PageSection>
</template>
